// Story 7.18 (ADO-1387), redesign — the presentational result panel for the scan
// flow (AC5/AC6/AC7/AC8/AC12). Deliberately camera-free so every UI state is
// unit-testable without getUserMedia/BarcodeDetector: ScanModal owns the camera +
// decode loop and drives this with a `ScanPhase`.
//
// Redesign layering: the `scanning` and `resolving` phases render NOTHING here —
// ScanModal draws their reticle + caption directly over the live feed. This panel
// owns only the surfaces that sit ON TOP of the (dimmed) feed: the confirm/
// disambiguate bottom-sheet and the centred message card. Everything stays inside
// ScanModal's single fullscreen `Modal`, so there is never a second `aria-modal`.

import { useEffect, useRef, useState } from 'react';
import { Button, ConflictMessage, Icon } from '../../design-system';
import { useBasketStore } from '../../basket/useBasketStore';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import { AddPromptModal, useAddPrompt } from '../../basket/useAddPrompt';
import { isAtLineMaximum, MAX_LINE_QUANTITY } from '../../basket/lineQuantity';
import { conflictView } from '../../basket/conflictCopy';
import type { SearchResultProduct } from '../../api/search.client';
import {
  NotAvailableProductCard,
  PRODUCT_NOT_AVAILABLE_MESSAGE,
} from '../Browse/NotAvailableProductCard';
import { cameraErrorCopy, SCAN_COPY, type ScanPhase } from './scanResolution';
import styles from './ScanModal.module.css';

/** Upper bound on the confirm-sheet quantity. Story 8.2 (ADO-2011), AC3: the add
 *  carries its quantity in ONE request, so the old 20-unit cap (which bounded N
 *  sequential +1 round-trips) is now the line maximum the server accepts. */
const MAX_SCAN_QTY = MAX_LINE_QUANTITY;

/** Focus a freshly-rendered surface (confirm sheet / message card) so keyboard + AT
 *  users are moved onto it when it appears on a camera-driven transition (AC12). */
function useFocusOnMount<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

export interface ScanResultPanelProps {
  readonly phase: ScanPhase;
  /** Disambiguation choice (AC5) — the user picks; the system never auto-picks. */
  readonly onSelectCandidate: (product: SearchResultProduct) => void;
  /** Resume scanning (message "Try again", confirm/disambiguate "Done"). Re-arms
   *  the decode loop on the still-live stream so the scanner stays open for the
   *  next product (review 2026-08-05: multi-item scanning). */
  readonly onRetry: () => void;
  /** Fall back to text search (AC7 / AC12 non-camera path). Takes NO argument — the
   *  scanned barcode is structurally unable to follow the user into the search box
   *  (AC8). */
  readonly onUseTextSearch: () => void;
  /** Fired after a FULLY successful add so ScanModal can show its "Added to cart"
   *  toast, arm the re-detect guard, and resume scanning (SCAN-05/06). It carries what
   *  the add put in the cart (for the toast's `Qty {n} · {product}`), never the barcode:
   *  the re-detect key is the RAW decode, which only ScanModal (the decode loop owner)
   *  holds; the panel only carries the resolved, normalised barcode, so it must not be
   *  the source of that key (see ScanModal.handleAdded). Optional so the panel is
   *  testable in isolation. */
  readonly onAdded?: (added: ScanAdded) => void;
}

/** What one successful scanner add put in the cart: the quantity THAT add carried (not
 *  the line's new total) and the product's display name. */
export interface ScanAdded {
  readonly quantity: number;
  readonly productName: string;
}

/** A lean product row — image (or the branded placeholder) + name + pack size.
 *  Price/category are intentionally omitted: neither exists on `ProductSummary`
 *  (pricing is deferred — basket D-2 / Story 8.2), so the sheet shows only real
 *  fields rather than the mockup's placeholder €/deposit/category chip. A product the
 *  server marked `notAvailable` (Enabler E7-C, ADO-2274, D-6) says so under its name. */
function ScanProductSummary({ product }: { product: SearchResultProduct }) {
  return (
    <div className={styles.product}>
      {product.imageUrl ? (
        <img className={styles.productImg} src={product.imageUrl} alt="" />
      ) : (
        <span className={styles.productImgPlaceholder} aria-hidden>
          <Icon name="camera" size={20} />
        </span>
      )}
      <span className={styles.productText}>
        <span className={styles.productName}>{product.name}</span>
        {product.packSize ? <span className={styles.productPack}>{product.packSize}</span> : null}
        {product.notAvailable === true ? (
          <span className={styles.productPack}>{PRODUCT_NOT_AVAILABLE_MESSAGE}</span>
        ) : null}
      </span>
    </div>
  );
}

/** Single-match confirm (AC6) as a bottom-sheet (SCAN-04): product + a functional
 *  quantity stepper + Add, routed through the shared Story 7.9 one-click-add path
 *  (`upsell: false` — the scanner stays open, so firing the app-wide Linked Product
 *  upsell here would stack a second aria-modal over the live viewport; `toast: false`:
 *  some screen readers ignore a live region outside an aria-modal dialog, so ScanModal
 *  draws and announces the "Added to cart" toast inside the dialog instead).
 *
 *  Quantity: Story 8.2 (ADO-2011), AC3 — the chosen quantity is ONE add request
 *  (`add(sku, quantity)`, summed server-side onto any existing line). Guarded:
 *    • `addingRef` — a SYNC double-click guard (the `adding` state re-render lags a
 *      fast second tap, so the button's `disabled` alone can't stop a second add);
 *    • `mountedRef` — if the sheet is dismissed / the modal closes mid-add, no stray
 *      `onAdded`/toast/resume fires after the user left;
 *    • failure — a blocked/failed add writes nothing, so no success toast fires and
 *      the inline error (the server's message) shows instead.
 *  Before an add of 19 or more, `useAddPrompt` asks SL1.0's confirmation. */
function ScanConfirmPanel({
  product,
  onScanAnother,
  onAdded,
}: {
  product: SearchResultProduct;
  onScanAnother: () => void;
  onAdded?: ((added: ScanAdded) => void) | undefined;
}) {
  // Scan add fires NO upsell (`upsell: false`, unchanged). `upsellVariant` is a
  // required field but inert here — a value is supplied only to satisfy the type.
  // No app-wide toast either (`toast: false`): ScanModal shows its own inside the dialog.
  const { confirmed, confirm, startedNewCart } = useAddConfirmation(product.sku, {
    productName: product.name,
    upsellVariant: 'toast',
    upsell: false,
    toast: false,
  });
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);
  const mountedRef = useRef(true);
  const sheetRef = useFocusOnMount<HTMLDivElement>();
  const basketPending = useBasketStore((s) => s.pending.has(product.sku));
  const basketError = useBasketStore((s) => s.mutationErrors.get(product.sku) ?? null);
  // Story 8.3 (ADO-2012), D-20: an add refused because the line changed since it was seen.
  // The sheet shows the Conflict Message with no action in its error slot; its copy carries
  // the number, since nothing else in the sheet shows it. "Add to basket" is the retry.
  const lineConflict = useBasketStore((s) => s.lineConflicts.get(product.sku));
  const conflict = lineConflict === undefined ? null : conflictView(lineConflict);
  const conflictMessage = conflict === null || conflict.kind === 'none' ? null : conflict.message;
  const dismissConflict = useBasketStore((s) => s.dismissConflict);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Leaving the sheet (Done, close, scan another) ends its message (D-20).
      if (useBasketStore.getState().lineConflicts.has(product.sku)) {
        useBasketStore.getState().dismissConflict(product.sku);
      }
    };
  }, [product.sku]);
  // Changing the picker ends the message too (D-20).
  const pickQuantity = (next: (current: number) => number) => {
    if (conflictMessage !== null) dismissConflict(product.sku);
    setQuantity(next);
  };

  const add = async (addQuantity: number) => {
    if (addingRef.current) return; // sync double-click guard (E4)
    addingRef.current = true;
    setAdding(true);
    // A failed/blocked add records an error for THIS SKU and writes nothing — no
    // success signal (the inline error shows instead). So does an add refused as a conflict
    // (Story 8.3, D-20), which `confirm` reports as not landed too.
    const failed = !(await confirm(addQuantity));
    addingRef.current = false;
    if (!mountedRef.current) return; // no state/onAdded on a dead sheet (E1/E3)
    setAdding(false);
    if (!failed) onAdded?.({ quantity: addQuantity, productName: product.name });
  };
  // SL1.0 parity: the scan route adds through the same `doAddToCart()` as the listing
  // (`cabinet_smanager.htm:399-400`), so an add of 19 or more asks first. Cancel adds
  // nothing and keeps the chosen quantity. An add of a product already in the cart is
  // not asked about (PO decision 2026-10-02, a deviation from SL1.0).
  const addPrompt = useAddPrompt((confirmedQuantity) => void add(confirmedQuantity));

  return (
    <div
      className={styles.sheet}
      role="group"
      aria-label="Scanned product"
      tabIndex={-1}
      ref={sheetRef}
    >
      <div className={styles.sheetHeader}>
        <span className={styles.sheetTitle}>
          <Icon name="check" size={18} />
          Barcode scanned
        </span>
        <button
          type="button"
          className={styles.sheetClose}
          aria-label="Dismiss and keep scanning"
          onClick={() => onScanAnother()}
        >
          <Icon name="x" size={18} />
        </button>
      </div>
      <ScanProductSummary product={product} />
      <div className={styles.confirmActions}>
        <div className={styles.stepper} role="group" aria-label="Quantity">
          <button
            type="button"
            className={styles.stepBtn}
            aria-label="Decrease quantity"
            disabled={quantity <= 1}
            onClick={() => pickQuantity((q) => Math.max(1, q - 1))}
          >
            <Icon name="minus" size={18} />
          </button>
          <span className={styles.qtyValue} aria-live="polite">
            {quantity}
          </span>
          <button
            type="button"
            className={styles.stepBtn}
            aria-label="Increase quantity"
            disabled={isAtLineMaximum(quantity)}
            onClick={() => pickQuantity((q) => Math.min(MAX_SCAN_QTY, q + 1))}
          >
            <Icon name="plus" size={18} />
          </button>
        </div>
        <Button
          variant="primary"
          className={styles.addBtn}
          onClick={() => addPrompt.request(quantity)}
          disabled={basketPending || adding}
          aria-label={`Add ${product.name} to basket`}
        >
          {confirmed ? 'Added ✓' : 'Add to basket'}
        </Button>
      </div>
      <Button variant="secondary" className={styles.doneBtn} onClick={() => onScanAnother()}>
        Done
      </Button>
      <span className={styles.visuallyHidden} role="status">
        {/* Story 8.3 (ADO-2012), card 11 (D-25): the toast is outside this dialog, so its words
            are said here too. */}
        {confirmed
          ? `${startedNewCart ? 'Started a new cart. ' : ''}Added ${product.name} to basket`
          : ''}
      </span>
      {basketError ? (
        <p className={styles.error} role="alert">
          {basketError}
        </p>
      ) : null}
      {conflictMessage === null ? null : <ConflictMessage message={conflictMessage} />}
      {/* Opened over the scanner's own fullscreen Modal, like the cart panel's Clear
          confirm: the shared Modal hands Escape and Tab to the topmost dialog. */}
      <AddPromptModal prompt={addPrompt} productName={product.name} />
    </div>
  );
}

/** Multi-match disambiguation (AC5) as a bottom-sheet: the operator MUST actively
 *  choose (the system never auto-picks). Focused + labelled on appear so keyboard/AT
 *  users are moved onto it and told a choice is required after the camera-driven
 *  transition — the same focus-on-mount + role treatment the confirm/message panels
 *  get (AC12); without it this AC5 surface was the one silent, unfocused transition. */
function ScanDisambiguatePanel({
  products,
  onSelectCandidate,
  onScanAnother,
}: {
  products: readonly SearchResultProduct[];
  onSelectCandidate: (product: SearchResultProduct) => void;
  onScanAnother: () => void;
}) {
  const sheetRef = useFocusOnMount<HTMLDivElement>();
  return (
    <div
      className={styles.sheet}
      role="group"
      aria-label="More than one product matches this barcode — choose one"
      tabIndex={-1}
      ref={sheetRef}
    >
      <p className={styles.disambiguateHint}>
        More than one product uses this barcode. Choose the one you want:
      </p>
      <ul className={styles.candidateList} aria-label="Products matching the barcode">
        {products.map((product) => (
          <li key={product.sku}>
            <button
              type="button"
              className={styles.candidate}
              onClick={() => onSelectCandidate(product)}
            >
              <ScanProductSummary product={product} />
            </button>
          </li>
        ))}
      </ul>
      <Button variant="secondary" className={styles.doneBtn} onClick={() => onScanAnother()}>
        Done
      </Button>
    </div>
  );
}

/** A scanned product off sale in the selected range that has a substitute (Enabler E7-C,
 *  ADO-2274, D-6) as a bottom-sheet: search's not-available card (SL1.0's copy and "See the
 *  substitute", no Add or quantity), so the scan never offers an add the search box would not.
 *  The card's substitute Adds fire no upsell, as the confirm sheet's do not. Focused on appear
 *  (AC12); "Done" resumes scanning. */
function ScanNotAvailablePanel({
  product,
  onScanAnother,
}: {
  product: SearchResultProduct;
  onScanAnother: () => void;
}) {
  const sheetRef = useFocusOnMount<HTMLDivElement>();
  return (
    <div
      className={styles.sheet}
      role="group"
      aria-label="Scanned product not available"
      tabIndex={-1}
      ref={sheetRef}
    >
      <div className={styles.sheetHeader}>
        <span className={styles.sheetTitle}>
          <Icon name="check" size={18} />
          Barcode scanned
        </span>
        <button
          type="button"
          className={styles.sheetClose}
          aria-label="Dismiss and keep scanning"
          onClick={() => onScanAnother()}
        >
          <Icon name="x" size={18} />
        </button>
      </div>
      <NotAvailableProductCard product={product} layout="list" suppressUpsell />
      <Button variant="secondary" className={styles.doneBtn} onClick={() => onScanAnother()}>
        Done
      </Button>
    </div>
  );
}

/** A message state (AC7) as a centred card over the dimmed feed: distinct title +
 *  body + retry + text-search fallback. Focused on appear so keyboard/AT users land
 *  on it after a camera-driven transition (AC12). */
function ScanMessagePanel({
  title,
  body,
  onRetry,
  onUseTextSearch,
}: {
  title: string;
  body: string;
  onRetry: () => void;
  onUseTextSearch: () => void;
}) {
  const cardRef = useFocusOnMount<HTMLDivElement>();
  return (
    <div className={styles.messageCard} role="alert" tabIndex={-1} ref={cardRef}>
      <h3 className={styles.messageTitle}>{title}</h3>
      <p className={styles.messageBody}>{body}</p>
      <div className={styles.messageActions}>
        {/* Wrapped so the click event is NOT forwarded — onUseTextSearch takes no
            argument, so the scanned barcode structurally cannot follow the user into
            the search box (AC8). */}
        <Button variant="primary" className={styles.primaryAction} onClick={() => onRetry()}>
          Try again
        </Button>
        <Button variant="secondary" onClick={() => onUseTextSearch()}>
          Search by name
        </Button>
      </div>
    </div>
  );
}

export function ScanResultPanel({
  phase,
  onSelectCandidate,
  onRetry,
  onUseTextSearch,
  onAdded,
}: ScanResultPanelProps) {
  switch (phase.status) {
    // ScanModal draws the reticle + caption for these two phases over the feed.
    case 'scanning':
    case 'resolving':
      return null;
    case 'confirm':
      return <ScanConfirmPanel product={phase.product} onScanAnother={onRetry} onAdded={onAdded} />;
    case 'notAvailable':
      return <ScanNotAvailablePanel product={phase.product} onScanAnother={onRetry} />;
    case 'disambiguate':
      return (
        <ScanDisambiguatePanel
          products={phase.products}
          onSelectCandidate={onSelectCandidate}
          onScanAnother={onRetry}
        />
      );
    case 'invalid':
    case 'notFound':
    case 'discontinued':
    case 'resolveError':
    case 'cameraError': {
      const copy =
        phase.status === 'cameraError' ? cameraErrorCopy(phase.reason) : SCAN_COPY[phase.status];
      // Re-mount per DISTINCT message so useFocusOnMount ([] deps) re-fires: a "Try
      // again" that lands on a different message state would otherwise reconcile as a
      // prop update on the same instance and never move focus to the new message.
      const key = phase.status === 'cameraError' ? `cameraError:${phase.reason}` : phase.status;
      return (
        <ScanMessagePanel
          key={key}
          title={copy.title}
          body={copy.body}
          onRetry={onRetry}
          onUseTextSearch={onUseTextSearch}
        />
      );
    }
  }
}

export default ScanResultPanel;
